import { LocalTime } from "@js-joda/core";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import type {
  PatientWeeklyTemplateMappedShift,
  PatientWeeklyTemplateShift,
} from "./patientWeeklyTemplate.types";

const dayNames = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];

export function getPatientWeeklyTemplateDays(firstDayOfWeek = 0) {
  const days = [...dayNames];
  const normalizedFirstDay = Number.isFinite(firstDayOfWeek)
    ? Math.max(0, Math.min(6, Math.trunc(firstDayOfWeek)))
    : 0;

  return [...days.slice(normalizedFirstDay), ...days.slice(0, normalizedFirstDay)];
}

function getShiftName(shiftIdx: number) {
  const shiftNum = shiftIdx + 1;
  const lastTwoDigits = shiftNum % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${shiftNum}th`;
  }

  switch (shiftNum % 10) {
    case 1:
      return `${shiftNum}st`;
    case 2:
      return `${shiftNum}nd`;
    case 3:
      return `${shiftNum}rd`;
    default:
      return `${shiftNum}th`;
  }
}

function getNthShiftForDay(
  shifts: PatientWeeklyTemplateShift[],
  dayOfWeek: string,
  shiftIdx: number
) {
  return shifts.filter((shift) => shift.dayOfWeek === dayOfWeek)[shiftIdx] ?? null;
}

export function mapPatientWeeklyTemplateShifts(
  rawShifts: PatientWeeklyTemplateShift[],
  daysOfWeek: string[]
): PatientWeeklyTemplateMappedShift[] {
  const shifts: PatientWeeklyTemplateMappedShift[] = [];

  for (let shiftIdx = 0; shiftIdx < rawShifts.length; shiftIdx++) {
    const dailyTemplates = daysOfWeek.map((dayOfWeek) =>
      getNthShiftForDay(rawShifts, dayOfWeek, shiftIdx)
    );

    if (dailyTemplates.some(Boolean)) {
      shifts.push({
        name: getShiftName(shiftIdx),
        dailyTemplates,
      });
    }
  }

  return shifts;
}

export function formatPatientWeeklyTemplateDay(dayOfWeek: string) {
  return `${dayOfWeek.slice(0, 1)}${dayOfWeek.slice(1).toLowerCase()}s`;
}

export function formatPatientWeeklyTemplateTime(value: LocalTime | string | null | undefined) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  try {
    if (value instanceof LocalTime) {
      return dateFormatter.toTime(value);
    }

    return dateFormatter.toTime(LocalTime.parse(value.length === 5 ? value : value.slice(0, 5)));
  } catch {
    return String(value);
  }
}

export function formatPatientWeeklyTemplateDuration(
  startTime: LocalTime | string | null | undefined,
  endTime: LocalTime | string | null | undefined
) {
  if (!startTime || !endTime) {
    return "";
  }

  try {
    const start =
      startTime instanceof LocalTime
        ? startTime
        : LocalTime.parse(startTime.length === 5 ? startTime : startTime.slice(0, 5));
    const end =
      endTime instanceof LocalTime
        ? endTime
        : LocalTime.parse(endTime.length === 5 ? endTime : endTime.slice(0, 5));
    const startMinutes = start.hour() * 60 + start.minute();
    const endMinutes = end.hour() * 60 + end.minute();
    const durationMinutes =
      endMinutes > startMinutes ? endMinutes - startMinutes : endMinutes + 24 * 60 - startMinutes;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (minutes === 0) {
      return hours === 0 ? "0h" : `${hours}h`;
    }

    return `${hours}:${String(minutes).padStart(2, "0")}h`;
  } catch {
    return "";
  }
}
